import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Layout, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import * as publicActions from 'store/actions/PublicActions';
import PublicSignerView from './components/PublicSignerView';
import PublicApproverView from './components/PublicApproverView';
import PublicViewerView from './components/PublicViewerView';
import PublicCCView from './components/PublicCCView';
import TokenExpiredPage from './components/TokenExpiredPage';
import SuccessPage from './components/SuccessPage';

const { Content } = Layout;
const { Title, Text } = Typography;

const PublicDocumentAccess = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expired, setExpired] = useState(false);
    const [data, setData] = useState(null);
    const [actionCompleted, setActionCompleted] = useState(false);
    const [completedAction, setCompletedAction] = useState(null);

    const validateAndLoad = useCallback(async () => {
        if (!token) {
            setError('No access token provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // First validate the token
            const validationResult = await publicActions.validateDocumentToken(token);

            if (!validationResult.valid) {
                if (validationResult.expired) {
                    setExpired(true);
                } else {
                    setError(validationResult.error || 'Invalid access token');
                }
                setLoading(false);
                return;
            }

            // Then load the full document data
            const documentData = await publicActions.getPublicDocument(token);

            if (documentData.valid === false) {
                if (documentData.expired) {
                    setExpired(true);
                } else {
                    setError(documentData.error || 'Failed to load document');
                }
                setLoading(false);
                return;
            }

            setData(documentData);
        } catch (err) {
            console.error('Error validating token:', err);
            setError('Failed to validate access. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        validateAndLoad();
    }, [validateAndLoad]);

    // Handle successful completion of action (signing, approving, etc.)
    const handleActionComplete = (action) => {
        setActionCompleted(true);
        setCompletedAction(action);
    };

    // Render loading state
    if (loading) {
        return (
            <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
                <Content style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 40
                }}>
                    <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                        size="large"
                    />
                    <Title level={4} style={{ marginTop: 24, color: '#666' }}>
                        Validating access...
                    </Title>
                    <Text type="secondary">
                        Please wait while we verify your access to this document.
                    </Text>
                </Content>
            </Layout>
        );
    }

    // Render expired/invalid token state
    if (expired || error) {
        return (
            <TokenExpiredPage
                expired={expired}
                error={error}
            />
        );
    }

    // Render success page after action completion
    if (actionCompleted) {
        return (
            <SuccessPage
                action={completedAction}
                documentName={data?.document?.title || data?.document?.name}
                recipientName={data?.recipient?.name}
            />
        );
    }

    // Render role-specific view based on recipient role
    const role = data?.recipient?.role || data?.recipient?.recipient_role;

    switch (role) {
        case 'SIGNER':
            return (
                <PublicSignerView
                    {...data}
                    token={token}
                    onComplete={() => handleActionComplete('signed')}
                />
            );

        case 'APPROVER':
            return (
                <PublicApproverView
                    {...data}
                    token={token}
                    onComplete={(action) => handleActionComplete(action)}
                />
            );

        case 'VIEWER':
            return (
                <PublicViewerView
                    {...data}
                    token={token}
                />
            );

        case 'CC':
            return (
                <PublicCCView
                    {...data}
                    token={token}
                />
            );

        default:
            return (
                <TokenExpiredPage
                    error={`Unknown role: ${role}. Please contact the document sender.`}
                />
            );
    }
};

export default PublicDocumentAccess;
